import {
  getInspectionScopeTotals,
  getTargetDeviation,
} from 'features/libra/inspectionPlanning/inspectionScopeTotals'
import {
  getPlanningStrategy,
  getRequiredInspectionArea,
} from 'features/libra/inspectionPlanning/planningStrategy'
import {
  InspectionArea,
  PlanningStrategies,
} from 'features/libra/inspectionPlanning/planningTypes'
import {
  formatScope,
  scopeBaseUnit,
} from 'features/libra/inspectionPlanning/scopeMeasurement'
import {
  ScopeType,
  TechniqueCatalogue,
} from 'features/libra/inspectionPlanning/techniqueTypes'
import { ParsedInspectionRecommendations } from 'features/libra/stepComponents/niiReport/inspectionRecommendations'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { GeckoMeasurementSystem } from 'types'

import { buildMethodWorkPackSections } from './buildMethodWorkPackSections'

export const quantityText = (value: number | undefined, unit: string) =>
  value === undefined
    ? 'Not specified'
    : `${value.toLocaleString('en-GB', { maximumFractionDigits: 3 })} ${unit}`

export function buildWorkPackZones(
  zones: ZoneParams[],
  areas: InspectionArea[],
  catalogue: TechniqueCatalogue,
  strategies: PlanningStrategies,
  recommendations: ParsedInspectionRecommendations,
  measurementSystem = GeckoMeasurementSystem.Metric
) {
  const scopeText = (value: number | undefined, scopeType: ScopeType) =>
    formatScope(
      value === undefined
        ? undefined
        : { num: value, unit: scopeBaseUnit(scopeType).id },
      scopeType,
      measurementSystem,
      catalogue.units.count
    )
  return zones.map((zone, zoneIndex) => {
    const strategyConfig = getPlanningStrategy(zone, strategies)
    const requiredArea = getRequiredInspectionArea(zone, strategies)
    const zoneAreas = areas.filter((area) => area.sourceZoneId === zone.id)
    return {
      sourceZoneId: zone.id,
      title: `Z${zoneIndex + 1} - ${zone.title}`,
      sections: [
        {
          title: 'Source zone requirements',
          rows: [
            { label: 'Zone reference', value: zone.id },
            {
              label: 'Strategy / required coverage',
              value: `${strategyConfig.strategyType ?? 'Not specified'} / ${strategyConfig.plannedCoveragePercent === undefined ? 'Not specified' : `${strategyConfig.plannedCoveragePercent}%`}`,
            },
            {
              label: 'Required physical area',
              value: scopeText(requiredArea, 'area'),
            },
            {
              label: 'Damage mechanisms',
              value:
                zone.damage_mechanisms.map((item) => item.name).join(', ') ||
                'Not specified',
            },
            {
              label: 'Recommendations',
              value:
                recommendations.perZone.find((item) => item.zone_id === zone.id)
                  ?.recommendation_text ||
                zone.zone_recommendations?.join('\n') ||
                'Not specified',
            },
            ...(strategyConfig.guidance
              ? [{ label: 'Planning guidance', value: strategyConfig.guidance }]
              : []),
          ],
        },
        ...zoneAreas.flatMap((area) =>
          buildMethodWorkPackSections(
            area,
            areas.indexOf(area),
            catalogue,
            measurementSystem
          )
        ),
        {
          title: 'Zone totals by degradation and technique',
          rows: [
            {
              label: 'Measurement basis',
              value:
                'Declared quantities; overlaps and repeated views are not deducted. These are not achieved inspection results.',
            },
            ...getInspectionScopeTotals(zoneAreas, catalogue).map((total) => ({
              label: `${catalogue.degradationTypes.find((item) => item.id === total.degradationTypeId)?.label ?? 'Unclassified'} / ${total.method}`,
              value: `Estimate: ${scopeText(total.estimated, total.scopeType)}${getTargetDeviation(total, requiredArea) ? `; ${getTargetDeviation(total, requiredArea)} source required area - review allocation and overlap` : ''}`,
            })),
          ],
        },
      ],
    }
  })
}
